import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FieldChange, JsonSchema} from './schema-form.types';
import {SchemaFieldComponent} from './schema-field.component';
import {matchesVisibleWhen} from './widget-registry';

/**
 * Entry point for a manifest-schema-driven form (e.g. a module's `settingsSchema`).
 *
 * Iterates the schema's top-level properties and renders one recursive
 * `app-schema-field` per property. Emits `fieldChange {key, value}` per top-level
 * property, preserving the existing `__settings.<key>` persistence granularity.
 * Everything shown is derived from the schema — no per-module code.
 */
@Component({
  selector: 'app-schema-form',
  standalone: true,
  imports: [SchemaFieldComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-4 w-full">
      @for (field of fields; track field.key) {
        <app-schema-field
          [schema]="field.schema"
          [value]="valueAt(field.key)"
          [resetKey]="resetKey"
          (valueChange)="fieldChange.emit({key: field.key, value: $event})">
        </app-schema-field>
      }
    </div>
  `,
})
export class SchemaFormComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: Record<string, any> | null = null;
  /** Kontext-Kennung (im Editor: die Knoten-ID) — verwirft den Anzeigezustand der Felder. */
  @Input() resetKey: string | null = null;
  @Output() fieldChange = new EventEmitter<FieldChange>();

  get fields(): { key: string; schema: JsonSchema }[] {
    const props = this.schema?.properties ?? {};
    // `x-hidden` blendet eine Einstellung aus, ohne sie aus dem Schema zu nehmen: der gespeicherte
    // Wert bleibt erhalten (z.B. Max Tokens der KI-Anweisung). Gleiche Regel wie in schema-field.
    // `x-visible-when` gegen die Geschwister-Einstellungen, wie in Array-Einträgen.
    return Object.keys(props)
      .filter(key => props[key]?.['x-hidden'] !== true && matchesVisibleWhen(props[key], this.value ?? {}))
      .map(key => ({key, schema: props[key]}));
  }

  /** Current value for a property, falling back to the schema default for display. */
  valueAt(key: string): any {
    const current = this.value?.[key];
    if (current !== undefined && current !== null && current !== '') return current;
    return this.schema?.properties?.[key]?.default ?? current;
  }
}
